// node_modules
import { FC, useCallback, useContext, useRef, useState } from "react";
// Types
import { TActionObjectDTO, TSavedQueryTempDTO } from "Types";
// styles
import styles from "./actionObjects.module.scss";
// Components
import { FindestRefTextBox } from "Components";
import { ActionObject } from "./ActionObject";
// Enums
import { GroupTermFunctionTypeEnum, SearchPriorityEnum, SearchSubTermTypeEnum } from "Enums";
// Controllers
import { ActionObjectControllerSingleton } from "Controllers";
// Contexts
import { SavedQueryContext, UserContext } from "Providers";
import { CaseContext } from "Stores";
// Custom hooks
import { useActionObjects } from "Hooks";
import { SearchTermGroupSearch } from "../SearchTermGroupSearch";
import { FeatureToggleConstants } from "Constants";

export const ActionObjects: FC = () => {
    // Ref
    const newActionObjectInputRef = useRef<HTMLInputElement>(null);
    
    // Contexts
    const { savedQuery, actionObjects, updateSavedQuery, duplicateSavedQuery } = useContext(SavedQueryContext);
    const { caseState } = useContext(CaseContext);
    const { isCurrentUserFindest } = useContext(UserContext);

    // State
    const [searchTermSearchQuery, setSearchTermSearchQuery] = useState<{query: string} | undefined>(undefined);
    const [searchTermElementType, setSearchTermElementType] = useState(GroupTermFunctionTypeEnum.Action);
    const [currentActionTermGroupId, setCurrentActionTermGroupId] = useState<string | undefined>(undefined);

    // Custom hooks
    const [addActionObject, getDuplicatedActionObjectFromValueInSavedQuery] = useActionObjects(
        caseState.selectedCaseId, caseState.selectedOriginalTechnologyId, savedQuery,
        updateSavedQuery, duplicateSavedQuery
    );

    // Determine if the user can use the search term groups
    const isSearchTermGroupsEnabled = FeatureToggleConstants.SynonymGroups && isCurrentUserFindest;

    // Logic
    const getActionObjectByIdInList = (id: number, actionObjectList: TActionObjectDTO[]): TActionObjectDTO | undefined => {
        // safety-checks
        if (id === null || id === undefined || !actionObjectList) {return undefined;}

        for (const actionObject of actionObjectList) {
            if (actionObject.id === id) {return actionObject;}
        }

        return undefined;
    };

    const addNewActionObject = async (_value?: string, objectTermGroupId?: string) => {
        if (!newActionObjectInputRef.current) { return; }

        await addActionObject(newActionObjectInputRef.current.value, currentActionTermGroupId,
            objectTermGroupId);

        // empty add new action object input
        newActionObjectInputRef.current.value = "";
        setSearchTermSearchQuery(undefined);
    };

    const changeActionObjectSearchPriority = async (id: number, actionObjectNewSearchPriority: SearchPriorityEnum): Promise<void> => {
        // safety-checks
        if (!savedQuery) {return;}

        const foundActionObject: TActionObjectDTO | undefined = getActionObjectByIdInList(id, actionObjects);
        // safety-checks
        if (!foundActionObject) {return;}

        if (savedQuery.isUserAdded && savedQuery.isOwnedByCurrentUser) {
            foundActionObject.searchPriority = actionObjectNewSearchPriority;
            const isSuccess = await ActionObjectControllerSingleton.updateAsync(foundActionObject, true);
            // update action object list in the saved query context
            if (isSuccess) {
                for (const actionObject of actionObjects) {
                    if (actionObject.id === id) {
                        actionObject.searchPriority = actionObjectNewSearchPriority;
                    }
                }

                savedQuery.actionObjects = [...actionObjects];
                updateSavedQuery(savedQuery);
            }
        } else {
            duplicateSavedQuery(caseState.selectedOriginalTechnologyId, caseState.selectedCaseId, async (duplicatedSavedQuery: TSavedQueryTempDTO): Promise<void> => {
                // safety-checks
                if (!foundActionObject || !duplicatedSavedQuery) {return;}

                const duplicatedActionObject: TActionObjectDTO | undefined =
                    getDuplicatedActionObjectFromValueInSavedQuery(`${foundActionObject.action} ${foundActionObject.dobject}`, duplicatedSavedQuery);

                // safety-checks
                if (!duplicatedActionObject) {return;}

                duplicatedActionObject.searchPriority = actionObjectNewSearchPriority;
                const isSuccess = await ActionObjectControllerSingleton.updateAsync(duplicatedActionObject, true);

                // update action object list in the saved query context
                if (isSuccess) {
                    for (const actionObject of duplicatedSavedQuery.actionObjects) {
                        if (actionObject.id === duplicatedActionObject.id) {
                            actionObject.searchPriority = duplicatedActionObject.searchPriority;
                        }
                    }

                    duplicatedSavedQuery.actionObjects = [...duplicatedSavedQuery.actionObjects];
                    updateSavedQuery(duplicatedSavedQuery);
                }
            });
        }
    };

    const changeActionObject = useCallback(async (id: number, text: string, searchSubTermType: SearchSubTermTypeEnum): Promise<void> => {
        // safety-checks
        if (!savedQuery) {return;}

        const foundActionObject: TActionObjectDTO | undefined = getActionObjectByIdInList(id, actionObjects);
        // safety-checks
        if (!foundActionObject) {return;}

        if (savedQuery.isUserAdded && savedQuery.isOwnedByCurrentUser) {
            if (searchSubTermType === SearchSubTermTypeEnum.Action) {
                foundActionObject.action = text;
            } else {
                foundActionObject.dobject = text;
            }
            const isSuccess = await ActionObjectControllerSingleton.updateAsync(foundActionObject, true);
            // update action object list in the saved query context
            if (isSuccess) {
                for (const actionObject of actionObjects) {
                    if (actionObject.id === id) {
                        if (searchSubTermType === SearchSubTermTypeEnum.Action) {
                            actionObject.action = text;
                        } else {
                            actionObject.dobject = text;
                        }
                    }
                }

                savedQuery.actionObjects = [...actionObjects];
                updateSavedQuery(savedQuery);
            }
        } else {
            duplicateSavedQuery(caseState.selectedOriginalTechnologyId, caseState.selectedCaseId, async (duplicatedSavedQuery: TSavedQueryTempDTO): Promise<void> => {
                // safety-checks
                if (!foundActionObject || !duplicatedSavedQuery) {return;}

                const duplicatedActionObject: TActionObjectDTO | undefined =
                    getDuplicatedActionObjectFromValueInSavedQuery(`${foundActionObject.action} ${foundActionObject.dobject}`, duplicatedSavedQuery);

                // safety-checks
                if (!duplicatedActionObject) {return;}

                if (searchSubTermType === SearchSubTermTypeEnum.Action) {
                    duplicatedActionObject.action = text;
                } else {
                    duplicatedActionObject.dobject = text;
                }
                const isSuccess = await ActionObjectControllerSingleton.updateAsync(duplicatedActionObject, true);

                // update action object list in the saved query context
                if (isSuccess) {
                    for (const actionObject of duplicatedSavedQuery.actionObjects) {
                        if (actionObject.id === duplicatedActionObject.id) {
                            if (searchSubTermType === SearchSubTermTypeEnum.Action) {
                                actionObject.action = text;
                            } else {
                                actionObject.dobject = text;
                            }
                        }
                    }

                    duplicatedSavedQuery.actionObjects = [...duplicatedSavedQuery.actionObjects];
                    updateSavedQuery(duplicatedSavedQuery);
                }
            });
        }
    }, [actionObjects, caseState.selectedCaseId, caseState.selectedOriginalTechnologyId, duplicateSavedQuery, getDuplicatedActionObjectFromValueInSavedQuery, savedQuery, updateSavedQuery]);

    const deleteActionObject = async (id: number): Promise<void> => {
        // safety-checks
        if (!savedQuery) {return;}

        const newActionObjectList: TActionObjectDTO[] = [];
        const foundActionObject: TActionObjectDTO | undefined = getActionObjectByIdInList(id, actionObjects);
        // safety-checks
        if (!foundActionObject) {return;}

        if (savedQuery.isUserAdded && savedQuery.isOwnedByCurrentUser) {
            const isSuccess = await ActionObjectControllerSingleton.deleteAsync(id, true);
            // update action object list in the saved query context
            if (isSuccess) {
                for (const actionObject of actionObjects) {
                    if (actionObject.id !== id) {
                        newActionObjectList.push(actionObject);
                    }
                }

                savedQuery.actionObjects = [...newActionObjectList];
                updateSavedQuery(savedQuery);
            }
        } else {
            duplicateSavedQuery(caseState.selectedOriginalTechnologyId, caseState.selectedCaseId, async (duplicatedSavedQuery: TSavedQueryTempDTO): Promise<void> => {
                // safety-checks
                if (!foundActionObject || !duplicatedSavedQuery) {return;}

                const duplicatedActionObject: TActionObjectDTO | undefined =
                    getDuplicatedActionObjectFromValueInSavedQuery(`${foundActionObject.action} ${foundActionObject.dobject}`, duplicatedSavedQuery);

                // safety-checks
                if (!duplicatedActionObject) {return;}

                const isSuccess = await ActionObjectControllerSingleton.deleteAsync(duplicatedActionObject.id, true);
                // update action object list in the saved query context
                if (isSuccess) {
                    for (const actionObject of duplicatedSavedQuery.actionObjects) {
                        if (actionObject.id !== duplicatedActionObject.id) {
                            newActionObjectList.push(actionObject);
                        }
                    }

                    duplicatedSavedQuery.actionObjects = [...newActionObjectList];
                    updateSavedQuery(duplicatedSavedQuery);
                }
            });
        }
    };

    const onEnter = (text: string) => {
        if (!text || text.trim().split(" ").length <= 1) { return; }
        addNewActionObject(text);
    };

    // Search term group logic
    const resetTermGroupIds = () => {
        setCurrentActionTermGroupId(undefined);
    };

    const onNewActionObjectChange = (text: string): void => {
        if(text.trimStart().indexOf(" ") > 0) {
            setSearchTermElementType(GroupTermFunctionTypeEnum.Object);
            setSearchTermSearchQuery({ query: text.split(" ").splice(1).join("") });
        } else {
            setSearchTermElementType(GroupTermFunctionTypeEnum.Action);
            setSearchTermSearchQuery({ query: text });
            resetTermGroupIds();
        }
    };

    const onSingleItemClick = (text: string): void => {
        if(!newActionObjectInputRef.current) return;

        if(text.trimStart().indexOf(" ") > 0) {
            newActionObjectInputRef.current.value = text;
            addNewActionObject();
        } else {
            const value = `${text} `;
            newActionObjectInputRef.current.value = value;
            resetTermGroupIds();
            onNewActionObjectChange(value);
        }
    };

    const onGroupClick = (groupId: string, groupName: string): void => {
        if(!newActionObjectInputRef.current) return;
        const text = newActionObjectInputRef.current.value;

        if(text.trimStart().indexOf(" ") > 0) {
            addNewActionObject(undefined, groupId);
        } else {
            const value = `${groupName} `;
            newActionObjectInputRef.current.value = value;
            onNewActionObjectChange(value);
            setCurrentActionTermGroupId(groupId);
            newActionObjectInputRef.current.focus();
        }
    };

    return (
        <div className={styles.userFlowDataList} style={{ marginBottom: "2rem" }}>
            <h5 className={styles.dataListTitle}>Functions</h5>
            <FindestRefTextBox ref={newActionObjectInputRef} isBorderless={true} placeholder={"Action Object <Enter>"} 
                onChange={onNewActionObjectChange} onEnter={onEnter} />
            { isSearchTermGroupsEnabled && searchTermSearchQuery ? 
                <SearchTermGroupSearch query={searchTermSearchQuery} elementType={searchTermElementType.toString()}
                    onSingleItemClick={onSingleItemClick} onGroupClick={onGroupClick} /> 
                : null
            }
            {actionObjects.map((actionObject) => {
                return (
                    <ActionObject
                        key={actionObject.id}
                        actionObject={actionObject}
                        changeActionObjectSearchPriority={changeActionObjectSearchPriority}
                        deleteActionObject={deleteActionObject}
                        editSearchSubTerm={changeActionObject} />
                );
            })}
        </div>
    );
};
